import { useEffect, useRef, useState } from 'react';
import { Bookmark, Gauge, Hash, ListTodo, Sparkles, Target, Type } from 'lucide-react';
import { prefersReducedMotion } from '../hooks';

interface Segment {
  text: string;
  /** Index into MARKS — highlights when its step is reached. */
  mark?: number;
}

/**
 * One free-form entry, written the way somebody actually writes at the
 * end of a day. Nothing in it is tagged, dated, or listed by hand.
 */
const ENTRY: Segment[] = [
  { text: 'Standup went sideways again. ' },
  {
    text: 'Got defensive about the timeline instead of just saying it slipped — better after lunch, but it sat with me.',
    mark: 0,
  },
  { text: ' ' },
  { text: 'Need to get the Q3 deck over to Amara before Friday.', mark: 1 },
  { text: ' Sara called out of nowhere — ' },
  { text: "she's moving to Lisbon in October", mark: 2 },
  { text: ', which I keep forgetting to tell anyone. And I said again that I want to ' },
  { text: 'run three mornings a week', mark: 3 },
  { text: '. Third week of saying it.' },
];

interface Card {
  icon: typeof Type;
  tone: string;
  kicker: string;
  value: string;
  meta?: string;
  /** Which highlight in the entry this was pulled from, if any. */
  mark?: number;
}

/**
 * What comes back. Card order follows the reading order of the entry,
 * so the highlight and the card that came from it land together.
 */
const CARDS: Card[] = [
  { icon: Type, tone: 'ink', kicker: 'title', value: 'Rough standup, better afternoon' },
  { icon: Gauge, tone: 'g-ember', kicker: 'sentiment', value: 'Mixed', meta: 'tense → settled', mark: 0 },
  { icon: ListTodo, tone: 'g-blue', kicker: 'task', value: 'Send Q3 deck to Amara', meta: 'due Fri', mark: 1 },
  { icon: Bookmark, tone: 'g-violet', kicker: 'memory', value: 'Sara → Lisbon, October', mark: 2 },
  { icon: Target, tone: 'g-sun', kicker: 'goal', value: 'Run three mornings a week', meta: 'week 3', mark: 3 },
  { icon: Hash, tone: 'g-pink', kicker: 'tags', value: 'work · health · people' },
];

/** mark index → the step at which it lights up. */
const MARK_STEP = new Map<number, number>(
  CARDS.flatMap((c, i) => (c.mark === undefined ? [] : [[c.mark, i] as [number, number]])),
);

/**
 * The signature element: one entry on top, the structure it produced
 * underneath. Each phrase gets marker-penned as the card it became
 * drops in, so the causal link is visible rather than claimed.
 *
 * Under prefers-reduced-motion the finished state renders immediately.
 */
export function MindFlowExtract(): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const isStatic = prefersReducedMotion();

  const [step, setStep] = useState(isStatic ? CARDS.length : 0);
  const [started, setStarted] = useState(isStatic);

  // Hold until the panel is actually on screen — the whole point is
  // watching it happen.
  useEffect(() => {
    if (started) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started || isStatic || step >= CARDS.length) return;
    const t = window.setTimeout(() => setStep((n) => n + 1), step === 0 ? 700 : 900);
    return () => window.clearTimeout(t);
  }, [started, isStatic, step]);

  return (
    <div
      ref={ref}
      className="mf-panel"
      role="img"
      aria-label="Example of one free-form journal entry from which MindFlow extracts a title, the entry's sentiment, a task with a due date, a memory about a friend moving to Lisbon, a goal to run three mornings a week, and topic tags."
    >
      <div className="mf-head" aria-hidden="true">
        <span>Tue 14 Aug · one entry</span>
        <span className="mf-head-note">nothing filed by hand</span>
      </div>

      <p className="mf-entry" aria-hidden="true">
        {ENTRY.map((seg, i) =>
          seg.mark === undefined ? (
            <span key={i}>{seg.text}</span>
          ) : (
            <mark
              key={i}
              className={`hl-${seg.mark} ${step > (MARK_STEP.get(seg.mark) ?? 0) ? 'on' : ''}`}
            >
              {seg.text}
            </mark>
          ),
        )}
      </p>

      <div className="mf-out" aria-hidden="true">
        <div className="mf-out-head">
          <Sparkles size={13} strokeWidth={2} />
          Gemini read it back
        </div>
        <div className="mf-cards">
          {CARDS.map((c, i) => (
            <div key={c.kicker} className={`mf-card ${i < step ? 'shown' : ''}`}>
              <span className={`mf-ic ${c.tone}`}>
                <c.icon size={14} strokeWidth={2} />
              </span>
              <span className="mf-card-text">
                <span className="mf-kicker">{c.kicker}</span>
                <span className="mf-val">{c.value}</span>
              </span>
              {c.meta && <span className="mf-meta">{c.meta}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
